import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { PaletteUtils } from '@shared/utils';

interface IButtonProps {
  // BASE
  className?: string;
  color?: 'primary' | 'outlined' | 'icon' | 'dashed';
  fullWidth?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => void;
  children: React.ReactNode;
  // LINK
  to?: string;
  // BUTTON
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
}

export const Button = (props: IButtonProps) => {
  const className = clsx(
    // BASE
    'flex items-center justify-center gap-3 appearance-none whitespace-nowrap transition-all duration-200 ease-in-out relative',
    PaletteUtils.isInsigniaPalette && 'rounded-sm',
    PaletteUtils.isVcfcPalette && 'rounded-3xl',
    // PRIMARY
    props.color === 'primary' && [
      // BASE PRIMARY
      'h-10 py-2 px-4',
      // INSIGNIA
      PaletteUtils.isInsigniaPalette && [
        'border-[transparent] bg-soft-black dark:bg-primary text-primary dark:text-soft-black hover:bg-white-signal-black active:bg-transport-black hover:shadow-lg active:shadow-md dark:hover:bg-pale-taupe dark:active:bg-gray-beige',
        props.disabled && 'opacity-50',
      ],
      // VCFC
      PaletteUtils.isVcfcPalette && [
        'border-[transparent] bg-primary hover:bg-primary-light dark:hover:bg-primary-darkest active:bg-primary text-ice',
        props.disabled && '!bg-light !text-dark-wet-asphalt',
      ],
    ],
    // OUTLINED
    props.color === 'outlined' && [
      // BASE OUTLINED
      'h-10 py-2 px-4 border',
      // INSIGNIA
      PaletteUtils.isInsigniaPalette && [
        'border-soft-black dark:border-primary bg-ice dark:bg-eerie-black text-soft-black dark:text-primary hover:bg-gainsborough active:bg-light-gray hover:shadow-lg active:shadow-md dark:hover:bg-signal-dark-black dark:active:bg-white-signal-black dark:hover:shadow-lg dark:active:shadow-md',
        props.disabled && 'opacity-50',
      ],
      // VCFC
      PaletteUtils.isVcfcPalette && [
        'border-primary bg-white hover:bg-ice active:bg-white dark:bg-transparent dark:hover:bg-dark-white dark:active:bg-transparent text-primary',
        props.disabled && 'opacity-40',
      ],
    ],
    // DASHED
    props.color === 'dashed' && [
      // BASE DASHED
      'h-10 py-2 px-4 border border-dashed',
      // INSIGNIA
      PaletteUtils.isInsigniaPalette && [
        'border-almost-gray text-slate dark:text-white hover:bg-smoky-white active:bg-gray-gainsborough hover:text-white-signal-black active:text-almost-black hover:shadow-lg active:shadow-md dark:hover:bg-signal-dark-black dark:active:bg-white-signal-black dark:hover:text-light-telegray dark:active:text-pearlescent-light-gray',
        props.disabled && 'opacity-50',
      ],
      // VCFC
      PaletteUtils.isVcfcPalette && [
        'border-primary dark:border-white bg-white hover:bg-ice active:bg-white dark:bg-transparent dark:hover:bg-dark-white dark:active:bg-transparent text-primary dark:text-white',
        props.disabled && '!border-dark-wet-asphalt opacity-50',
      ],
    ],
    // ICON
    props.color === 'icon' && [
      'h-fit p-0 bg-[transparent] inline-flex items-center justify-center text-center hover:opacity-80 active:opacity-70',
      props.disabled && 'opacity-50',
    ],
    // DEFAULT
    !props.color && [
      'h-10 py-2 px-4 text-soft-black dark:text-white hover:text-white-signal-black active:text-almost-black dark:hover:text-light-telegray dark:active:text-pearlescent-light-gray',
      props.disabled && 'opacity-50',
    ],
    props.disabled && 'pointer-events-none',
    props.fullWidth && 'w-full',
    props.className,
  );

  if (props.to) {
    return (
      <Link className={className} to={props.to} onClick={props.onClick}>
        {props.children}
      </Link>
    );
  }
  return (
    <button className={className} type={props.type || 'button'} disabled={props.disabled} onClick={props.onClick}>
      {props.children}
    </button>
  );
};
